import { useCallback, useEffect, useState } from 'react';

import { useScrollEndPonyfill } from './useScrollEndPonyfill';
import { useCurrentViewport } from './useCurrentViewport';

interface UseCurrentSlideIndexProps {
  slidesElement: HTMLUListElement | null;
  slideElement: HTMLLIElement | null;
  initialIndex?: number | null;
}

export const useInitialIndex = ({ slidesElement, slideElement, initialIndex }: UseCurrentSlideIndexProps) => {
  useEffect(() => {
    if (!initialIndex) return;

    const currentScollPosition = slidesElement?.scrollLeft ?? 0;
    const itemWidth = slideElement?.clientWidth ?? 0;

    slidesElement?.scroll({
      left: currentScollPosition + itemWidth * initialIndex,
      behavior: 'instant' as ScrollBehavior,
    });
  }, [initialIndex, slidesElement, slideElement]);
};

interface UseCurrentSlideIndexProps {
  slidesElement: HTMLUListElement | null;
  slideElement: HTMLLIElement | null;
  initialIndex?: number | null;
}

const useCurrentSlideIndex = ({ slidesElement, slideElement, initialIndex }: UseCurrentSlideIndexProps) => {
  const [currentSliderIndex, setCurrentSliderIndex] = useState<number>(initialIndex || 0);
  const currentViewport = useCurrentViewport();

  const getCurrentIndex = (scrollLeft: number, slideWidth: number) => {
    if (!slideWidth && !scrollLeft) return 0;

    return Math.round(scrollLeft / slideWidth);
  };

  const checkSliderState = useCallback(() => {
    const scrollLeft = slidesElement?.scrollLeft ?? 0;
    const slideWidth = slideElement ? parseInt(window.getComputedStyle(slideElement).width, 10) : 0;

    setCurrentSliderIndex(getCurrentIndex(scrollLeft, slideWidth));
  }, [slidesElement, slideElement]);

  useScrollEndPonyfill(slidesElement, checkSliderState);

  useEffect(() => {
    checkSliderState();
  }, [checkSliderState, currentViewport]);

  return currentSliderIndex;
};

interface UseSliderButtonStateProps {
  slidesElement: HTMLUListElement | null;
}

const useSliderButtonState = ({ slidesElement }: UseSliderButtonStateProps) => {
  const [previousButtonDisabled, setBeforeButtonDisabled] = useState(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
  const currentViewport = useCurrentViewport();

  const checkSliderState = useCallback(() => {
    const scrollLeft = slidesElement?.scrollLeft ?? 0;
    const sliderWidth = slidesElement?.clientWidth ?? 0;
    const sliderScrollWidth = slidesElement?.scrollWidth ?? 0;

    if (scrollLeft === 0) setBeforeButtonDisabled(true);
    if (scrollLeft > 0) setBeforeButtonDisabled(false);

    if (sliderScrollWidth - Math.round(scrollLeft) - 1 <= sliderWidth) {
      return setNextButtonDisabled(true);
    }

    return setNextButtonDisabled(false);
  }, [slidesElement]);

  useScrollEndPonyfill(slidesElement, checkSliderState);

  useEffect(() => {
    checkSliderState();
  }, [checkSliderState, currentViewport]);

  return [previousButtonDisabled, nextButtonDisabled];
};

type UseCustomSliderProps = UseSliderButtonStateProps & UseCurrentSlideIndexProps;

export const useCustomSlider = ({ slidesElement, slideElement, initialIndex }: UseCustomSliderProps) => {
  useInitialIndex({
    initialIndex,
    slidesElement,
    slideElement,
  });

  const [previousButtonDisabled, nextButtonDisabled] = useSliderButtonState({
    slidesElement,
  });

  const currentSliderIndex = useCurrentSlideIndex({
    slidesElement,
    slideElement,
    initialIndex,
  });

  return { previousButtonDisabled, nextButtonDisabled, currentSliderIndex };
};
